import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { InputTextarea } from 'primereact/inputtextarea';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import moment from 'moment';
import { Dropdown } from 'primereact/dropdown';
import './QueueForm.css';
import { apiCallErrorHandler } from '../utils/common';

// Constants
const VIN_REGEX = /([A-HJ-NPR-Z0-9]{17})/gi;
const DEFAULT_SOURCE = 'Partslink24';
const API_BASE_URL = process.env.REACT_APP_API_URL || '';

// Partslink brand options (required when source is Partslink24)
const PARTSLINK_BRANDS = [
  "abarth",
  "alfa romeo",
  "audi",
  "bmw",
  "citroen",
  "citroen (DS)",
  "cupra",
  "dacia",
  "fiat",
  "fiat (commercial)",
  "ford",
  "ford (commercial)",
  "hyundai",
  "jeep",
  "mercedes",
  "mitsubishi",
  "nissan",
  "opel",
  "peugeot",
  "renault",
  "seat",
  "skoda",
  "toyota",
  "vw",
  "vw (commercial)",
  "volvo"
].map(b => ({ label: b.charAt(0).toUpperCase() + b.slice(1), value: b }));

// Helper functions
const detectSourceFromRoute = (pathname) => {
  if (!pathname) return DEFAULT_SOURCE;
  if (pathname.includes('queue-myrenault')) return 'MyRenault';
  if (pathname.includes('queue-partslink')) return 'Partslink24';
  return DEFAULT_SOURCE;
};

// Maps the UI source label to the value stored in VinQueue.Source on the backend.
const toBackendSourceValue = (source) => (source === 'Partslink24' ? 'partslink' : source.toLowerCase());

const parseVinsFromText = (input) => {
  if (!input) return [];
  const matches = Array.from(input.matchAll(VIN_REGEX), (m) => m[1].toUpperCase());
  return Array.from(new Set(matches));
};

const buildFiltersPayload = (filters) => {
  const payload = {};
  if (!filters) return payload;

  Object.keys(filters).forEach((key) => {
    const filter = filters[key];
    if (!filter) return;
    const value = Array.isArray(filter) ? filter[0]?.value : filter.value;
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      payload[key] = value;
    }
  });

  return payload;
};

const booleanOptions = [
  { label: "Yes", value: true },
  { label: "No", value: false },
];

// Main component
export default function QueueForm() {
  const location = useLocation();
  const toast = useRef(null);
  const { getAccessTokenSilently } = useAuth0();
  useEffect(() => { document.title = 'OptiConfig VIN Scraping Queue'; }, []);

  const [text, setText] = useState('');
  const source = useMemo(() => detectSourceFromRoute(location.pathname), [location.pathname]);
  const [rows, setRows] = useState([]);
  const [parsedVins, setParsedVins] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [brand, setBrand] = useState(null);
  const [lazyParams, setLazyParams] = useState({
    first: 0,
    rows: 50,
    sortField: "queuedAt",
    sortOrder: -1,
    filters: {
      isProcessed: { value: null, matchMode: 'equals' },
      isSucceeded: { value: null, matchMode: 'equals' },
    },
  });
  const [totalRecords, setTotalRecords] = useState(0);

  // Load data on route or param change
  useEffect(() => {
    loadLazyData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, lazyParams]);

  // reset brand when source changes
  useEffect(() => {
    if (source !== 'Partslink24') setBrand(null);
  }, [source]);

  useEffect(() => {
    setParsedVins(parseVinsFromText(text));
  }, [text]);

  const showToast = (severity, summary, detail) => {
    if (toast.current) {
      toast.current.show({ severity, summary, detail, life: 3000 });
    }
  };

  const loadLazyData = useCallback(async () => {
    setFetching(true);
    try {
      const token = await getAccessTokenSilently();

      // Use current lazyParams for API call
      const page = Math.floor(lazyParams.first / lazyParams.rows);
      const qs = new URLSearchParams({
        page: String(page),
        size: String(lazyParams.rows),
      });

      // Handle sorting
      let effectiveSortOrder = null;
      if (lazyParams.sortOrder !== null && lazyParams.sortOrder !== undefined) {
        if (typeof lazyParams.sortOrder === 'number') {
          effectiveSortOrder = lazyParams.sortOrder === 1 ? 'asc' : 'desc';
        } else if (typeof lazyParams.sortOrder === 'string') {
          const s = lazyParams.sortOrder.toLowerCase().trim();
          effectiveSortOrder = s === 'asc' ? 'asc' : 'desc';
        }
      }

      if (lazyParams.sortField) qs.append('sortField', lazyParams.sortField);
      if (effectiveSortOrder) qs.append('sortOrder', effectiveSortOrder);

      // Handle filters
      const filtersObj = buildFiltersPayload(lazyParams.filters);
      filtersObj.source = toBackendSourceValue(source);
      qs.append('filters', JSON.stringify(filtersObj));

      // Call queue endpoint; backend returns { Data: [...], Total: N }
      const url = `${API_BASE_URL}/queue?${qs.toString()}`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) await apiCallErrorHandler(res, 'Failed to load queue');

      const payload = await res.json();

      // Support new QueueResponse shape: { Data: [...], Total: N }
      const items = Array.isArray(payload) ? payload : (payload.Data ?? payload.data ?? payload.items ?? []);
      const total = typeof payload.Total === 'number'
        ? payload.Total
        : (typeof payload.total === 'number' ? payload.total : (Array.isArray(items) ? items.length : 0));

      setRows(items);
      setTotalRecords(total);

      // Ensure paginator 'first' is within range after server returned total
      const rowsPerPage = lazyParams.rows;
      const currentFirst = lazyParams.first;
      const maxFirst = rowsPerPage > 0
        ? Math.max(0, Math.floor((Math.max(0, total) - 1) / rowsPerPage) * rowsPerPage)
        : 0;
      
      if (currentFirst > maxFirst) {
        setLazyParams((prev) => ({ ...prev, first: maxFirst }));
      }
    } catch (err) {
      showToast('error', 'Error', err.message || 'Failed to load queue');
      setRows([]);
      setTotalRecords(0);
    } finally {
      setFetching(false);
    }
  }, [getAccessTokenSilently, lazyParams, source]);

  // Separate handlers for different events
  const onPage = (event) => {
    setLazyParams(prev => ({
      ...prev,
      first: event.first,
      rows: event.rows,
    }));
  };

  const onSort = (event) => {
    const currentSortOrder = lazyParams.sortOrder;
    const currentSortField = lazyParams.sortField;
    
    let newSortOrder = event.sortOrder;
    
    // Handle sort toggle if clicking the same field
    if (event.sortField === currentSortField) {
      newSortOrder = currentSortOrder === 1 ? -1 : 1;
    }

    setLazyParams(prev => ({
      ...prev,
      first: 0, // Reset to first page when sorting
      sortField: event.sortField,
      sortOrder: newSortOrder,
    }));
  };

  const onFilter = (event) => {
    setLazyParams(prev => ({
      ...prev,
      first: 0, // Reset to first page when filtering
      filters: event.filters,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const vinsToQueue = parsedVins.length ? parsedVins : text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (!vinsToQueue.length) return;

    setSubmitting(true);
    try {
      const token = await getAccessTokenSilently();
      
      const endpointSegment = toBackendSourceValue(source);


      const url = `${API_BASE_URL}/queue/add/${endpointSegment}`;

      // validation server-side guard
      if (source === 'Partslink24' && !brand) {
        showToast('error', 'Validation', 'Brand is required for Partslink24');
        setSubmitting(false);
        return;
      }

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          vins: vinsToQueue,
          ...(source === 'Partslink24' ? { brand } : {})
        }),
      });

      if (!response.ok) await apiCallErrorHandler(response, 'Queue request failed');

      const data = await response.json();
      showToast('success', 'Success', `Queued ${data?.acceptedVins?.length || vinsToQueue.length} VIN(s)`);
      await loadLazyData();
      setText('');
    } catch (err) {
      showToast('error', 'Error', err.message || 'Queue request failed');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    return dateString ? moment.utc(dateString).local().format('DD.MM.YY HH:mm') : '';
  };

  const renderHeader = (label, field) => {
    const isSorted = lazyParams.sortField === field;
    const order = lazyParams.sortOrder;
    const arrow = isSorted
      ? (order === 1 || String(order).toLowerCase() === 'asc' ? '▲' : '▼')
      : '';
    return (
      <div className="col-header">
        <span className="col-label">{label}</span>
        <span className="col-sort">{arrow}</span>
      </div>
    );
  };

  return (
    <div className="queue-form card">
      <Toast ref={toast} />
      <h3 style={{ textAlign: 'center', marginBottom: '1rem' }}>Queue - {source}</h3>

      <form className="queue-form__form" onSubmit={handleSubmit}>
        <label className="p-mb-2">Paste VINs</label>
        <div className="queue-split">
          <div className="queue-split__left">
            <InputTextarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              autoResize
              className="w-full"
              placeholder="Paste VINs or text containing VINs (one per line or anywhere in text)"
            />
          </div>
          <div className="queue-split__right">
            <div className="vin-list-header">
              <span>Parsed VINs</span>
              <span className="vin-count">{parsedVins.length}</span>
            </div>
            <div className="vin-list-body">
              {parsedVins.length === 0 ? (
                <div className="vin-empty">No VINs parsed</div>
              ) : (
                parsedVins.map((v) => (
                  <div key={v} className="vin-item">
                    {v}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        {source === 'Partslink24' && (
            <div style={{ marginTop: '0.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <label style={{ marginRight: '0.5rem', fontSize: '0.9rem' }}>Brand<span style={{ color: '#d00', marginLeft: 6 }}>*</span></label>
              <Dropdown
                value={brand}
                options={PARTSLINK_BRANDS}
                onChange={(e) => setBrand(e.value)}
                placeholder="Select brand"
                className="p-inputtext-sm"
                style={{ minWidth: 160 }}
              />
            </div>
          )}
        <div className="queue-form__controls p-mt-2">
          <Button
            type="submit"
            label={submitting ? 'Queuing...' : 'Queue'}
            className="p-button-primary"
            disabled={
              parsedVins.length === 0 ||
              submitting ||
              (source === 'Partslink24' && !brand)
            }
          />
        </div>
      </form>

      <div className="p-mt-4">
        <DataTable
          value={rows}
          className="queue-table"
          loading={fetching}
          first={lazyParams.first}
          rows={lazyParams.rows}
          totalRecords={totalRecords}
          onPage={onPage}
          onSort={onSort}
          onFilter={onFilter}
          paginator
          rowsPerPageOptions={[10, 25, 50]}
          sortMode="single"
          filterDisplay="row"
          lazy
          header={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span>Filtered rows: {totalRecords}</span>
              <span style={{ fontSize: '0.85rem', color: '#666' }}>
                Showing {rows.length} on this page
              </span>
            </div>
          }
        >
          <Column field="id" header="Id" hidden />
          <Column field="vin" header={renderHeader('VIN', 'vin')} sortable filter filterPlaceholder="Filter by VIN" />
          <Column field="brand" header={renderHeader('Brand', 'brand')} sortable filter filterPlaceholder="Filter by brand" />
          <Column field="queuedAt" header={renderHeader('Queued At', 'queuedAt')} body={(row) => formatDate(row.queuedAt)} sortable />
          <Column field="queuedBy" header={renderHeader('Queued By', 'queuedBy')} sortable filter filterPlaceholder="Filter by user" />
          <Column header="Processed At" body={(row) => formatDate(row.processedAt)} />
          <Column
            field="isProcessed"
            sortable
            filter
            showFilterMenu={false}
            filterElement={(options) => (
              <Dropdown
                value={options.value}
                options={booleanOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Select"
                className="p-column-filter"
                showClear
              />
            )}
            header={renderHeader('Is Processed', 'isProcessed')}
            body={(row) => (row.isProcessed ? "Yes" : "No")}
          />

          <Column
            field="isSucceeded"
            sortable
            filter
            showFilterMenu={false}
            filterElement={(options) => (
              <Dropdown
                value={options.value}
                options={booleanOptions}
                onChange={(e) => options.filterApplyCallback(e.value)}
                placeholder="Select"
                className="p-column-filter"
                showClear
              />
            )}
            header={renderHeader('Is Succeeded', 'isSucceeded')}
            body={(row) => (row.isSucceeded ? "Yes" : "No")}
          />

          <Column field="remarks" header="Remarks" />
        </DataTable>
      </div>
    </div>
  );
}